import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './App.css';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, onSnapshot, query, where, doc, getDoc } from 'firebase/firestore';
import UserProfileModal from './components/UserProfileModal';

import { CATEGORIES, BUSINESS_ROLES } from './constants';
import FilterBar from './components/FilterBar';
import MapClickHandler from './components/MapClickHandler';
import LoginModal from './components/LoginModal';
import UserWriteModal from './components/UserWriteModal';
import BusinessWriteModal from './components/BusinessWriteModal';

function MapLocationMarker({ position }) {
  const map = useMap();
  useEffect(() => {
    if (!position) return;
    const marker = L.marker(position, {
      icon: L.divIcon({
        className: '',
        html: `
          <div style="display:flex;flex-direction:column;align-items:center;width:160px;">
            <div style="
              background:white;
              border-radius:12px;
              padding:6px 12px;
              font-size:12px;
              white-space:nowrap;
              box-shadow:0 2px 10px rgba(0,0,0,0.18);
              color:#333;
              font-family:sans-serif;
              font-weight:600;
              position:relative;
              margin-bottom:6px;
            ">
              지금 여기에 뭐가있나요?
              <div style="
                position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);
                width:0;height:0;
                border-left:6px solid transparent;
                border-right:6px solid transparent;
                border-top:6px solid white;
              "></div>
            </div>
            <div style="
              width:14px;height:14px;
              background:#4A90E2;
              border-radius:50%;
              border:3px solid white;
              box-shadow:0 0 0 2px #4A90E2,0 2px 6px rgba(0,0,0,0.3);
            "></div>
          </div>
        `,
        iconSize: [160, 54],
        iconAnchor: [80, 54],
      })
    }).addTo(map);
    return () => map.removeLayer(marker);
  }, [position, map]);
  return null;
}

function LocationSetter({ position }) {
  const map = useMap();
  const centeredRef = useRef(false);
  useEffect(() => {
    if (position && !centeredRef.current) {
      centeredRef.current = true;
      map.setView(position, 17);
    }
  }, [position, map]);
  return null;
}

function MapTapHandler({ onTap, enabled }) {
  useMapEvents({
    click(e) {
      if (enabled) {
        onTap([e.latlng.lat, e.latlng.lng]);
      }
    }
  });
  return null;
}

export default function App() {
  const [position, setPosition] = useState(null);
  const [activeFilters, setActiveFilters] = useState(['all']);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [userDisplayName, setUserDisplayName] = useState('익명');
  const [showLogin, setShowLogin] = useState(false);
  const [showWrite, setShowWrite] = useState(null); // null | 'user' | 'business'
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const [pins, setPins] = useState([]);
  const [targetPostId, setTargetPostId] = useState(null);
  const [pickingLocation, setPickingLocation] = useState(false);
  const [pickedLocation, setPickedLocation] = useState(null);
  const [followingIds, setFollowingIds] = useState([]);
  const [profileTarget, setProfileTarget] = useState(null);

  const isBusiness = ['business_basic', 'business_pro', 'business_premium'].includes(userRole);
  const canPickLocation = ['business_pro', 'business_premium'].includes(userRole);
  const canAlwaysOn = userRole === 'business_premium';

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setPosition([pos.coords.latitude, pos.coords.longitude]),
      () => setPosition([37.5665, 126.9780])
    );
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const postId = params.get('postId');
    if (postId) setTargetPostId(postId);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const userDoc = await getDoc(doc(db, 'users', u.uid));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role || 'user');
          setUserDisplayName(userDoc.data().displayName || '익명');
        }
      } else {
        setUserRole('user');
        setUserDisplayName('익명');
        setFollowingIds([]);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'follows'), where('followerId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snap) => {
      setFollowingIds(snap.docs.map(d => d.data().followingId));
    });
    return unsubscribe;
  }, [user]);

  useEffect(() => {
    const q = query(
      collection(db, 'posts'),
      where('expiresAt', '>', new Date())
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setPins(data);
    });
    return unsubscribe;
  }, []);

  const handleToggle = (key) => {
    if (key === 'all') { setActiveFilters(['all']); return; }
    setActiveFilters(prev => {
      const without = prev.filter(f => f !== 'all');
      if (without.includes(key)) {
        const next = without.filter(f => f !== key);
        return next.length === 0 ? ['all'] : next;
      }
      return [...without, key];
    });
  };

  const handlePressStart = () => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      if (!user) { setShowLogin(true); return; }
      setShowWrite('business');
    }, 600);
  };

  const handlePressEnd = () => {
    clearTimeout(longPressTimer.current);
    if (!longPressTriggered.current) {
      if (!user) { setShowLogin(true); return; }
      setShowWrite('user');
    }
  };

  const handlePickLocationRequest = () => {
    setShowWrite(null);
    setPickingLocation(true);
  };

  const handleMapTap = (latlng) => {
    if (pickingLocation) {
      setPickedLocation(latlng);
      setPickingLocation(false);
      setShowWrite('business');
    }
  };

  const filteredPins = (() => {
    let result = pins;
    if (activeFilters.includes('following')) {
      result = result.filter(p => followingIds.includes(p.uid));
    }
    const categoryKeys = activeFilters.filter(f => f !== 'all' && f !== 'following');
    if (categoryKeys.length > 0) {
      result = result.filter(p => categoryKeys.includes(p.category));
    }
    return result;
  })();

  if (!position) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      위치 가져오는 중...
    </div>
  );

  return (
    <div className="app-root">
      {profileTarget && (
        <UserProfileModal
          targetUid={profileTarget.uid}
          targetName={profileTarget.displayName}
          currentUser={user}
          followingIds={followingIds}
          pins={pins}
          onClose={() => setProfileTarget(null)}
        />
      )}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showWrite === 'business' && user && (
        <BusinessWriteModal
          onClose={() => { setShowWrite(null); setPickedLocation(null); }}
          userPosition={pickedLocation || position}
          pickedLocation={pickedLocation}
          canPickLocation={canPickLocation}
          onPickLocation={handlePickLocationRequest}
          user={user}
          displayName={userDisplayName}
          canAlwaysOn={canAlwaysOn}
        />
      )}
      {showWrite === 'user' && user && (
        <UserWriteModal onClose={() => setShowWrite(null)} userPosition={position} user={user} displayName={userDisplayName} />
      )}

      {pickingLocation && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 9998, pointerEvents: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(0,0,0,0.75)', color: 'white',
            padding: '12px 20px', borderRadius: 12, fontSize: 15,
            pointerEvents: 'all',
          }}>
            📍 등록할 위치를 지도에서 탭하세요
            <button
              type="button"
              onClick={() => setPickingLocation(false)}
              style={{ marginLeft: 12, background: 'none', border: 'none', color: '#ccc', fontSize: 14, cursor: 'pointer' }}
            >
              취소
            </button>
          </div>
        </div>
      )}

      <div style={{ background: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
        <FilterBar
          activeFilters={activeFilters}
          onToggle={handleToggle}
          totalCount={filteredPins.length}
          user={user}
          displayName={userDisplayName}
          onLogout={() => signOut(auth)}
        />
      </div>

      <div style={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={position} zoom={17}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true} zoomAnimation={true}
          markerZoomAnimation={true} zoomControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap &copy; CARTO'
            className="map-tiles"
          />
          <LocationSetter position={position} />
          <MapLocationMarker position={position} />
          <MapTapHandler onTap={handleMapTap} enabled={pickingLocation} />
          <MapClickHandler
            pins={filteredPins}
            userPosition={position}
            targetPostId={targetPostId}
            currentUser={user}
            followingIds={followingIds}
            onUserClick={(uid, displayName) => setProfileTarget({ uid, displayName })}
          />
        </MapContainer>

        <div style={{
          position: 'absolute', bottom: 30, right: 20,
          zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        }}>
          <div style={{
            background: 'white', borderRadius: 10, padding: '6px 10px',
            fontSize: 12, whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            color: '#333', fontFamily: 'sans-serif', lineHeight: 1.7,
            position: 'relative',
          }}>
            짧게 터치 — 일상 기록<br />
            꾹 누르기 — 행사 등록
            <div style={{
              position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid white',
            }} />
          </div>
          <button
            type="button"
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={() => clearTimeout(longPressTimer.current)}
            onTouchStart={(e) => { e.preventDefault(); handlePressStart(); }}
            onTouchEnd={(e) => { e.preventDefault(); handlePressEnd(); }}
            style={{
              width: 56, height: 56, borderRadius: '50%',
              background: pickingLocation ? '#E74C3C' : '#333',
              color: 'white', border: 'none', fontSize: 28, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              userSelect: 'none', WebkitUserSelect: 'none',
            }}
          >
            ✏️
          </button>
        </div>
      </div>
    </div>
  );
}